import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { updateFormData, saveDossier } from "../../redux/dispatch";
import useRubriqueNav from "../../hooks/useRubriqueNav";
import RubriqueNav from "../shared/RubriqueNav";
import useConfidence from "../../hooks/useConfidence";
import ConfidenceLabel from "../shared/ConfidenceLabel";
import "./CartographieSites.css";
import toast from "react-hot-toast";
import SaveLoader from "../shared/SaveLoader";
import { API_BASE } from "../../config/api";
import noImage from "../../assets/no-image.svg";

const RUBRIQUE = "cartographie_sites";
const INITIAL = { bilan_imagerie: {}, bilan_biologie: {} };

const examensRadio = [
  { key: "radio_standard", label: "Radiographie standard" },
  { key: "echographie",    label: "Échographie" },
  { key: "tdm",            label: "TDM" },
  { key: "irm",            label: "IRM" },
  { key: "autre_radio",    label: "Autre examen radiologique" },
];

const examensBio = [
  { key: "hematologie",        label: "Hématologie",                hasPreciser: true },
  { key: "biochimie",          label: "Biochimie",                  hasPreciser: true },
  { key: "coagulation",        label: "Coagulation",                hasPreciser: true },
  { key: "groupe_sanguin",     label: "Groupe sanguin",             hasPreciser: true },
  { key: "infectieux",         label: "Infectieux / Microbiologie", hasPreciser: true },
  { key: "marqueurs_tumoraux", label: "Marqueurs tumoraux",         hasPreciser: true },
  { key: "cytologie",          label: "Cytologie",                  hasPreciser: true },
  { key: "myelogramme",        label: "Myélogramme(s)",             hasPreciser: true },
  { key: "lcr",                label: "LCR",                        hasPreciser: true },
  { key: "histologie",         label: "Histologie",                 hasPreciser: true },
  { key: "autres_bio",         label: "Autres examens biologiques", hasPreciser: true },
];

const CartographieSites = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id }   = useParams();
  const [saving, setSaving] = useState(false);

  const rawImages = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.images || []);
  const IMAGES = rawImages.length > 0
    ? rawImages.map((img) => `${API_BASE}/${img.chemin_image}`)
    : [noImage];

  const { currentDossier, list } = useSelector((s) => s.dossiers);
  const dossier = currentDossier || list.find((d) => d.id === id) || null;

  const savedImagerie = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.bilan_imagerie || {});
  const savedBiologie = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.bilan_biologie || {});

  const [imagerie, setImagerie] = useState({ ...INITIAL.bilan_imagerie, ...savedImagerie });
  const [biologie, setBiologie] = useState({ ...INITIAL.bilan_biologie, ...savedBiologie });
  const [imgIndex, setImgIndex] = useState(0);

  const imagerieRef = useRef(imagerie);
  const biologieRef = useRef(biologie);

  useEffect(() => { imagerieRef.current = imagerie; }, [imagerie]);
  useEffect(() => { biologieRef.current = biologie; }, [biologie]);

  useEffect(() => {
    setImagerie({ ...INITIAL.bilan_imagerie, ...savedImagerie });
    setBiologie({ ...INITIAL.bilan_biologie, ...savedBiologie });
  }, [id]);


// APRÈS
const { goNext, goPrev, goTo, suivante, precedente, indexCourant, total } = useRubriqueNav("cartographie_sites");
  // ── Confidence ───────────────────────────────────────────────────────────────
  const { getConfidence } = useConfidence(id, RUBRIQUE);
  const gcImg = (field) => getConfidence("bilan_imagerie", field);
  const gcBio = (field) => getConfidence("bilan_biologie", field);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const onChangeImagerie = (field, value) =>
    setImagerie((prev) => ({ ...prev, [field]: value }));
  const onChangeBiologie = (field, value) =>
    setBiologie((prev) => ({ ...prev, [field]: value }));

  const handleSave = () => {
    dispatch(updateFormData(id, RUBRIQUE, "bilan_imagerie", imagerieRef.current));
    dispatch(updateFormData(id, RUBRIQUE, "bilan_biologie", biologieRef.current));
  };

  useEffect(() => {
    return () => { handleSave(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveAll = async () => {
    setSaving(true);
    dispatch(updateFormData(id, RUBRIQUE, "bilan_imagerie", imagerie));
    dispatch(updateFormData(id, RUBRIQUE, "bilan_biologie", biologie));
    try {
      const result = await dispatch(saveDossier());
      toast.success(result.message);
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.message || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setImagerie({ ...INITIAL.bilan_imagerie, ...savedImagerie });
    setBiologie({ ...INITIAL.bilan_biologie, ...savedBiologie });
  };

  if (!dossier) {
    return (
      <div className="cs-no-dossier">
        <p>Aucun dossier sélectionné.</p>
        <button onClick={() => navigate("/")}>Retour à la liste</button>
      </div>
    );
  }

  return (
    <div className="cs-page">
      {saving && <SaveLoader />}

      <header className="cs-header">
        <div className="cs-header-left">
          <button className="cs-back-btn" onClick={() => navigate("/")}>← Retour à la liste</button>
          <div>
            <h1 className="cs-title">Dossier {dossier.numero_dossier}</h1>
            <p className="cs-subtitle">Patient : {dossier.patient} &nbsp;|&nbsp; Service : {dossier.service}</p>
          </div>
        </div>
        <button className="cs-save-btn" onClick={handleSaveAll} disabled={saving}>
          Sauvegarder les modifications
        </button>
      </header>

      <div className="cs-body">
        <aside className="cs-image-panel">
          <div className="cs-image-header">
            <span className="cs-rubrique-label">Cartographie des Sites Tumoraux</span>
            <span className="cs-image-counter">Image {imgIndex + 1} / {IMAGES.length}</span>
          </div>
          <div className="cs-image-viewer">
            <img src={IMAGES[imgIndex]} alt={`Page ${imgIndex + 1}`} className="cs-image" />
          </div>
          <div className="cs-image-nav">
            <button className="cs-nav-btn" onClick={() => setImgIndex((i) => Math.max(0, i - 1))} disabled={imgIndex === 0}>‹</button>
            <div className="cs-image-dots">
              {IMAGES.map((_, i) => (
                <span key={i} className={`cs-dot ${i === imgIndex ? "cs-dot--active" : ""}`} onClick={() => setImgIndex(i)} />
              ))}
            </div>
            <button className="cs-nav-btn" onClick={() => setImgIndex((i) => Math.min(IMAGES.length - 1, i + 1))} disabled={imgIndex === IMAGES.length - 1}>›</button>
          </div>
        </aside>

        <main className="cs-form-panel">
          <div className="cs-tab-content">
            <div className="cs-content-title">
              <span className="cs-content-title__child">Bilan Initial Demandé</span>
            </div>

            <div className="cs-fields">

              {/* ── Imagerie ── */}
              <div className="cs-section-label">Imagerie</div>
              {examensRadio.map(({ key, label }) => (
                <div key={key} className="cs-exam-row">
                  <div className="cs-exam-header">
                    <span className="cs-exam-label">
                      {label}
                      <ConfidenceLabel confidence={gcImg(key)} />
                    </span>
                    <div className="cs-oui-non">
                      <label className="cs-radio-label">
                        <input type="radio" name={key} value="oui"
                          checked={imagerie[key] === "oui"}
                          onChange={() => onChangeImagerie(key, "oui")} />
                        Oui
                      </label>
                      <label className="cs-radio-label">
                        <input type="radio" name={key} value="non"
                          checked={imagerie[key] === "non"}
                          onChange={() => onChangeImagerie(key, "non")} />
                        Non
                      </label>
                    </div>
                  </div>
                  {imagerie[key] === "oui" && (
                    <input type="text" className="cs-preciser-input"
                      placeholder="Préciser..."
                      value={imagerie[`${key}_preciser`] ?? ""}
                      onChange={(e) => onChangeImagerie(`${key}_preciser`, e.target.value)}
                      style={{ borderColor: gcImg(`${key}_preciser`).color || undefined }} />
                  )}
                </div>
              ))}

              {/* ── Biologie ── */}
              <div className="cs-section-label" style={{ marginTop: 20 }}>Biologie</div>
              {examensBio.map(({ key, label, hasPreciser }) => (
                <div key={key} className="cs-exam-row">
                  <div className="cs-exam-header">
                    <span className="cs-exam-label">
                      {label}
                      <ConfidenceLabel confidence={gcBio(key)} />
                    </span>
                    <div className="cs-oui-non">
                      <label className="cs-radio-label">
                        <input type="radio" name={key} value="oui"
                          checked={biologie[key] === "oui"}
                          onChange={() => onChangeBiologie(key, "oui")} />
                        Oui
                      </label>
                      <label className="cs-radio-label">
                        <input type="radio" name={key} value="non"
                          checked={biologie[key] === "non"}
                          onChange={() => onChangeBiologie(key, "non")} />
                        Non
                      </label>
                    </div>
                  </div>
                  {biologie[key] === "oui" && hasPreciser && (
                    <input type="text" className="cs-preciser-input"
                      placeholder="Préciser..."
                      value={biologie[`${key}_preciser`] ?? ""}
                      onChange={(e) => onChangeBiologie(`${key}_preciser`, e.target.value)}
                      style={{ borderColor: gcBio(`${key}_preciser`).color || undefined }} />
                  )}
                </div>
              ))}

              {/* ── Prémédication ── */}
              <div className="cs-section-label" style={{ marginTop: 20 }}>
                Prémédication
                <ConfidenceLabel confidence={gcBio("premedication")} />
              </div>
              <div className="cs-field-group">
                <textarea rows={4} placeholder="Décrire la prémédication prévue..."
                  value={biologie.premedication ?? ""}
                  onChange={(e) => onChangeBiologie("premedication", e.target.value)}
                  style={{ borderColor: gcBio("premedication").color || undefined }} />
              </div>

            </div>
          </div>

          {/* <div className="cs-form-actions">
            <button className="cs-btn-save" onClick={handleSave}>Enregistrer</button>
            <button className="cs-btn-cancel" onClick={handleCancel}>Annuler</button>
          </div> */}


<RubriqueNav goNext={goNext} goPrev={goPrev} goTo={goTo} suivante={suivante} precedente={precedente} indexCourant={indexCourant} total={total} />        </main>
      </div>
    </div>
  );
};

export default CartographieSites;